import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactCrop, {
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
  Crop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { setCanvasPreview, PixelCrop as PixelCropType } from "@/utils/setCanvasPreview";
import { UIImageCategory, UI_IMAGE_CATEGORIES, StandardCropData } from "@/modules/uiImages/types";
import LureoButton from "@/common/LureoButton";

interface ImageCropperProps {
  imageSrc: string;
  imageCategory: UIImageCategory;
  onCropComplete: (croppedImageUrl: string, cropData: StandardCropData) => Promise<void>;
  onCancel: () => void;
  className?: string;
  hideCancel?: boolean;
  fill?: boolean;
  caption?: React.ReactNode;
  aspect?: number | null;
  outputMimeType?: 'image/jpeg' | 'image/png';
}

const MIN_DIMENSION = 150;
const FREE_MIN_DIMENSION = 48;
const CAPTION_ALLOWANCE = 56;
const FILL_PAD_TOP = 48;
const FILL_PAD_BOTTOM = 8;

export function ImageCropper({
  imageSrc,
  imageCategory,
  onCropComplete,
  onCancel,
  className = "",
  hideCancel = false,
  fill = false,
  caption,
  aspect: aspectOverride,
  outputMimeType = 'image/jpeg'
}: ImageCropperProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [error, setError] = useState("");
  const [isCropping, setIsCropping] = useState(false);

  // A percentage cap resolves against a content-sized parent here, which distorts the image;
  // the box is measured instead so the cap is a real pixel value.
  const boxRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { setBoxHeight(el.clientHeight); setBoxWidth(el.clientWidth); });
    ro.observe(el);
    roRef.current = ro;
    setBoxHeight(el.clientHeight);
    setBoxWidth(el.clientWidth);
  }, []);

  useEffect(() => () => roRef.current?.disconnect(), []);

  const categoryConfig = UI_IMAGE_CATEGORIES[imageCategory];
  
  // Convert aspect ratio string to number
  const getAspectRatio = (): number | undefined => {
    if (aspectOverride === null) return undefined;
    if (aspectOverride !== undefined) return aspectOverride;
    switch (categoryConfig.aspectRatio) {
      case '9:16':
        return 9 / 16;
      case '16:9':
        return 16 / 9;
      case '1:1':
        return 1;
      default:
        return 16 / 9;
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height, naturalWidth, naturalHeight } = e.currentTarget;
    setNatural({ w: naturalWidth, h: naturalHeight });

    // Seed from whichever edge constrains the aspect, so the region opens fully extended.
    const aspect = getAspectRatio();
    if (aspect === undefined) {
      setCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 });
      setError("");
      return;
    }
    const crop = makeAspectCrop(
      aspect > width / height ? { unit: "%", width: 100 } : { unit: "%", height: 100 },
      aspect,
      width,
      height
    );
    const centeredCrop = centerCrop(crop, width, height);
    setCrop(centeredCrop);
    setError(""); // Clear any previous errors
  };

  const onImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Image failed to load:', e);
    setError('Failed to load image. This may be due to security restrictions.');
  };

  const fitStyle = (() => {
    if (!natural || !boxWidth || !boxHeight) return { visibility: 'hidden' as const };
    const availW = Math.max(0, boxWidth - 16);
    const availH = Math.max(0, boxHeight - FILL_PAD_TOP - FILL_PAD_BOTTOM - (caption ? CAPTION_ALLOWANCE : 0));
    const k = Math.min(availW / natural.w, availH / natural.h);
    return { width: `${Math.floor(natural.w * k)}px`, height: `${Math.floor(natural.h * k)}px` };
  })();

  const handleCropImage = async () => {
    if (!imgRef.current || !previewCanvasRef.current || !crop || isCropping) {
      return;
    }

    setIsCropping(true);
    setError(""); // Clear any previous errors

    try {
      // Create the cropped image using canvas
      setCanvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        convertToPixelCrop(
          crop,
          imgRef.current.width,
          imgRef.current.height
        ) as PixelCropType
      );
      
      // Get the cropped image as a data URL with JPEG quality control
      const croppedImageUrl = outputMimeType === 'image/png'
        ? previewCanvasRef.current.toDataURL('image/png')
        : previewCanvasRef.current.toDataURL('image/jpeg', 0.85);
      
      // Convert to standardized crop data format
      const pixelCrop = convertToPixelCrop(
        crop,
        imgRef.current.width,
        imgRef.current.height
      );
      
      const cropData: StandardCropData = {
        x: pixelCrop.x,
        y: pixelCrop.y,
        width: pixelCrop.width,
        height: pixelCrop.height
      };

      // Pass the cropped image URL and standardized crop data
      await onCropComplete(croppedImageUrl, cropData);
    } catch (error) {
      console.error('Error cropping image:', error);
      if (error instanceof Error && error.name === 'SecurityError') {
        setError('Cannot crop image due to security restrictions. Please try uploading the image again.');
      } else {
        setError('Failed to crop image. Please try again.');
      }
    } finally {
      setIsCropping(false);
    }
  };

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {error && (
        <p className="text-lureo-danger-ink text-lureo-12 text-center mb-2 leading-4">{error}</p>
      )}

      {imageSrc && (
        <div className="flex flex-col h-full">
          <div ref={boxRef} className={`relative flex flex-1 flex-col items-center justify-center min-h-0 ${fill ? '' : 'overflow-hidden max-h-[calc(60vh-120px)]'}`}>
            <div className={fill ? 'absolute inset-0 flex flex-col items-center justify-center gap-3 px-2 pb-2 pt-12' : 'contents'}>
            <ReactCrop
              crop={crop}
              onChange={(pixelCrop, percentCrop) => setCrop(percentCrop)}
              keepSelection
              aspect={getAspectRatio()}
              minWidth={aspectOverride === null ? FREE_MIN_DIMENSION : MIN_DIMENSION}
              minHeight={aspectOverride === null ? FREE_MIN_DIMENSION : undefined}
              circularCrop={imageCategory === 'ScanImage' || imageCategory === 'Avatar'}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                crossOrigin="anonymous"
                style={fill
                  ? fitStyle
                  : {
                      maxHeight: "calc(60vh - 200px)",
                      maxWidth: "100%",
                      objectFit: "contain",
                      height: "auto",
                      width: "auto",
                    }}
                onLoad={onImageLoad}
                onError={onImageError}
              />
            </ReactCrop>

            {caption && (
              <div className="shrink-0 px-4 text-center font-lureo-font-prose text-lureo-13 leading-[1.5] text-lureo-ink-subtle">{caption}</div>
            )}
            </div>
          </div>

          <div className="flex gap-2 justify-center px-4 pb-4 pt-3 shrink-0">
            {!hideCancel && (
              <LureoButton
                variant="outline-2"
                onClick={onCancel}
                disabled={isCropping}
                className="h-9 px-7 rounded-lureo-radius-button-pill bg-lureo-surface"
              >
                Cancel
              </LureoButton>
            )}
            <LureoButton
              variant="secondary"
              onClick={handleCropImage}
              disabled={isCropping}
              className="h-9 px-7 rounded-lureo-radius-button-pill"
            >
              {isCropping ? 'Cropping...' : 'Crop'}
            </LureoButton>
          </div>
        </div>
      )}

      {crop && (
        <canvas
          ref={previewCanvasRef}
          className="hidden"
          style={{
            border: "var(--lureo-border-width) solid black",
            objectFit: "contain",
          }}
        />
      )}
    </div>
  );
}
